import { useEffect, useState } from 'react';
import { rateApi, memberApi, type Rate, type Member } from '../../api';
import Select from '../../components/common/Select';

// Effective-dated hourly / daily / fixed rates. Every billable line pulls a
// rate here. Rates cannot be deleted once referenced by a time entry — set
// `effective_to` instead.

const UNITS = [
  { value: 'hour',  label: 'Per hour' },
  { value: 'day',   label: 'Per day' },
  { value: 'fixed', label: 'Fixed fee' },
];

export default function SettingsRates() {
  const [rates, setRates] = useState<Rate[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Rate | 'new' | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [r, m] = await Promise.all([rateApi.list(), memberApi.list().catch(() => ({ members: [] }))]);
      setRates(r.rates);
      setMembers(m.members);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const memberName = (user_id?: number | null) => {
    if (!user_id) return <span className="muted">Chambers default</span>;
    const m = members.find((x) => x.user_id === user_id);
    return m ? m.display_name : `#${user_id}`;
  };

  const remove = async (r: Rate) => {
    if (!confirm(`Delete rate "${r.name}"?`)) return;
    try { await rateApi.remove(r.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };

  const isTrue = (v: unknown) => v === true || v === 'true';

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <div className="row">
        <div className="grow">
          <h1>Rates</h1>
          <p className="muted">Effective-dated billing rates. A rate can be per-advocate or a chambers default (no advocate selected).</p>
        </div>
        <button className="primary" onClick={() => setEditing('new')}>+ New rate</button>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>Loading\u2026</div>
        ) : rates.length === 0 ? (
          <p className="muted" style={{ padding: 14 }}>No rates yet. Add your first hourly rate.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Advocate</th>
                <th>Unit</th>
                <th style={{ textAlign: 'right' }}>Rate (ZAR)</th>
                <th>Effective</th>
                <th>Status</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.ROWID}>
                  <td style={{ fontWeight: 500 }}>{r.name}</td>
                  <td>{memberName(r.user_id)}</td>
                  <td><span className="pill">{UNITS.find((u) => u.value === r.unit)?.label || r.unit}</span></td>
                  <td style={{ textAlign: 'right' }}>R {Number(r.rate_zar).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                  <td>
                    {r.effective_from}
                    {r.effective_to ? ` \u2192 ${r.effective_to}` : ''}
                  </td>
                  <td>{isTrue(r.active) ? <span className="pill success">active</span> : <span className="pill">paused</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => setEditing(r)}>Edit</button>
                    <button onClick={() => remove(r)} style={{ marginLeft: 6 }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <RateModal
          rate={editing === 'new' ? null : editing}
          members={members}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RateModal({
  rate, members, onClose, onSaved,
}: { rate: Rate | null; members: Member[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: rate?.name || '',
    activity_code: rate?.activity_code || '',
    user_id: rate?.user_id ?? null as number | null,
    unit: rate?.unit || 'hour',
    rate_zar: rate?.rate_zar ?? 0,
    effective_from: rate?.effective_from || new Date().toISOString().slice(0, 10),
    effective_to: rate?.effective_to || '',
    active: rate == null ? true : (rate.active === true || rate.active === 'true'),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const payload = { ...form, effective_to: form.effective_to || undefined };
      if (rate) await rateApi.update(rate.ROWID, payload as Partial<Rate>);
      else      await rateApi.create(payload as Partial<Rate>);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
        <h3>{rate ? 'Edit rate' : 'New rate'}</h3>
        <div className="stack">
          <div>
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard hourly" />
          </div>
          <div className="row">
            <div className="grow">
              <label>Advocate</label>
              <Select
                value={form.user_id != null ? String(form.user_id) : ''}
                onChange={(v) => setForm({ ...form, user_id: v ? Number(v) : null })}
                options={[
                  { value: '', label: 'Chambers default' },
                  ...members.map((m) => ({ value: String(m.user_id), label: m.display_name })),
                ]}
                searchPlaceholder="Search members\u2026"
              />
            </div>
            <div style={{ width: 180 }}>
              <label>Unit</label>
              <Select
                value={form.unit}
                onChange={(v) => setForm({ ...form, unit: v as Rate['unit'] })}
                options={UNITS}
              />
            </div>
          </div>
          <div className="row">
            <div style={{ width: 200 }}>
              <label>Rate (ZAR)</label>
              <input type="number" step="0.01" value={form.rate_zar} onChange={(e) => setForm({ ...form, rate_zar: Number(e.target.value) })} />
            </div>
            <div className="grow">
              <label>Activity code (optional)</label>
              <input value={form.activity_code} onChange={(e) => setForm({ ...form, activity_code: e.target.value })} placeholder="consultation / drafting / appearance" />
            </div>
          </div>
          <div className="row">
            <div className="grow">
              <label>Effective from</label>
              <input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
            </div>
            <div className="grow">
              <label>Effective to (optional)</label>
              <input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} style={{ width: 'auto' }} />
            <span>Active</span>
          </label>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving || !form.name}>
              {saving ? 'Saving\u2026' : (rate ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
